import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from "react-helmet"
import { Tag, Empty, Spin, Input, Collapse, Button } from "antd"
import { DownloadOutlined } from "@ant-design/icons"
import jsPDF from "jspdf"

import { ContentWrapper } from "components"
import { useBreadcrumbFilters, ALL_CONTRACTS_VALUE } from "hooks"
import { setLoader, setBreadcrumb, delayedDispatch } from "store/actions"
import { API, Endpoints } from "utils/api"
import {
	EXTERNAL_STATUS_COLORS,
	getExternalStatusLabel,
	ExternalDeliveryStatus,
} from "utils/status"
import Strings from "utils/strings"

import type { Route, OrderExternalDelivery, RoutePickup } from "../Routes/types"
import {
	getExternalReturnState,
	toExternalReturnEntries,
} from "../Routes/externalReturns"
import type {
	ExternalReturnEntry,
	ExternalReturnState,
} from "../Routes/externalReturns"
import ExternalReturnsProgress, {
	ExternalReturnTag,
} from "../Routes/components/ExternalReturnsProgress"

import {
	BATCHING_STATUS_COLORS,
	getBatchingStatusLabel,
} from "./batchingStatus"
import type { ExternalBatchingStatus } from "./batchingStatus"
import {
	BatchProgress,
	ExternalDeliveryFilters,
	WAREHOUSE_PICKUP_VALUE,
} from "./components"
import type { VanOption } from "./components"

import "./styles.scss"

const { Search } = Input

export interface BatchOrderDetail {
	orderId: string
	userName: string
	userPhone: string
	address: string
	status?: string
	dropoffEta?: string
	trackingUrl?: string
	deliveryStatus?: ExternalDeliveryStatus
	returnPincode?: string
	barcode?: string
	manifestItems?: { name?: string; quantity?: number }[]
	returnState?: ExternalReturnState | null
}

export interface ExternalBatch {
	internalBatchId: string
	partner: string
	batchingStatus?: ExternalBatchingStatus
	assignedAt?: string
	assignedBy?: string
	courier?: OrderExternalDelivery["courier"]
	pickupEta?: string
	dropoffEta?: string
	trackingUrl?: string
	latestStatus?: ExternalDeliveryStatus
	orderCount: number
	routeName: string
	routeId: string
	pickup?: RoutePickup
	orders: BatchOrderDetail[]
	// Failed deliveries in the batch, bundled orders included
	returnEntries: ExternalReturnEntry[]
	// Set when every order in the batch shares the same return pincode; otherwise
	// undefined and the pincode is shown per order instead.
	returnPincode?: string
}

function groupOrdersIntoBatches(routes: Route[]): ExternalBatch[] {
	const batchMap = new Map<string, ExternalBatch>()

	for (const route of routes) {
		const ext = route.externalDelivery
		if (!ext) continue

		const batchStatusByInternalId = new Map<
			string,
			ExternalBatchingStatus | undefined
		>()
		for (const b of ext.batches || []) {
			batchStatusByInternalId.set(b.internalBatchId, b.status)
		}

		for (const addr of route.addresses) {
			if (addr.isDepot || !addr.order) continue

			const orderExt = addr.order.externalDelivery
			if (!orderExt?.internalBatchId) continue

			const existing = batchMap.get(orderExt.internalBatchId)

			const orderDetail: BatchOrderDetail = {
				orderId: addr.order.orderId,
				userName: addr.order.user?.name || "-",
				userPhone: addr.order.user?.phone || "",
				address: addr.address || "-",
				status: addr.order.status,
				dropoffEta: orderExt.dropoffEta,
				trackingUrl: orderExt.trackingUrl,
				deliveryStatus: orderExt.status as ExternalDeliveryStatus | undefined,
				returnPincode: orderExt.returnPincode,
				barcode: orderExt.pickupVerification?.barcodes?.[0]?.value,
				manifestItems: orderExt.manifestItems,
				returnState: getExternalReturnState(addr.order),
			}
			const returnEntries = toExternalReturnEntries([addr.order])

			if (existing) {
				existing.orderCount++
				existing.orders.push(orderDetail)
				existing.returnEntries.push(...returnEntries)
				if (orderExt.courier) existing.courier = orderExt.courier
				if (orderExt.status)
					existing.latestStatus = orderExt.status as ExternalDeliveryStatus
				if (orderExt.pickupEta) existing.pickupEta = orderExt.pickupEta
				if (orderExt.dropoffEta) existing.dropoffEta = orderExt.dropoffEta
				if (orderExt.trackingUrl) existing.trackingUrl = orderExt.trackingUrl
			} else {
				// Route's batches[] is the primary source of batch lifecycle status.
				const batchingStatus = batchStatusByInternalId.get(
					orderExt.internalBatchId
				)
				batchMap.set(orderExt.internalBatchId, {
					internalBatchId: orderExt.internalBatchId,
					partner: orderExt.partner || ext.partner,
					batchingStatus,
					assignedAt: ext.assignedAt,
					assignedBy: ext.assignedBy,
					courier: orderExt.courier,
					pickupEta: orderExt.pickupEta,
					dropoffEta: orderExt.dropoffEta,
					trackingUrl: orderExt.trackingUrl,
					latestStatus: orderExt.status as ExternalDeliveryStatus | undefined,
					orderCount: 1,
					routeName: route.name || route.systemName,
					routeId: route._id,
					pickup: route.pickup,
					orders: [orderDetail],
					returnEntries,
				})
			}
		}
	}

	// Surface the return pincode once at the batch level only when EVERY order has
	// one and they're all identical; if any is missing or they differ, leave it
	// per order.
	const batches = Array.from(batchMap.values())
	for (const batch of batches) {
		const allHavePincode =
			batch.orders.length > 0 && batch.orders.every((o) => o.returnPincode)
		const pincodes = new Set(batch.orders.map((o) => o.returnPincode))
		if (allHavePincode && pincodes.size === 1) {
			batch.returnPincode = pincodes.values().next().value
		}
	}

	return batches
}

const ExternalDeliveries: React.FC = () => {
	const dispatch = useDispatch()

	const { breadcrumbActions } = useBreadcrumbFilters({
		autoSelectFirst: false,
	})

	const rawFilters = useSelector((state: any) => state.filters) || {}
	const filters = useMemo(
		() => ({
			business: rawFilters.business,
			// "All contracts" is a Routes-only concept (the global filter is
			// shared across screens) — this screen has no single contract to
			// scope by, so treat it the same as no contract selected.
			contract:
				rawFilters.contract === ALL_CONTRACTS_VALUE
					? null
					: rawFilters.contract,
			date: rawFilters.date,
		}),
		[rawFilters.business, rawFilters.contract, rawFilters.date]
	)

	const [batches, setBatches] = useState<ExternalBatch[]>([])
	const [loading, setLoading] = useState(false)
	const [searchTerm, setSearchTerm] = useState("")
	const [selectedStatus, setSelectedStatus] = useState<
		ExternalBatchingStatus[]
	>([])
	const [selectedVans, setSelectedVans] = useState<string[]>([])
	const isFetchingRef = useRef(false)

	const fetchData = useCallback(async () => {
		if (!filters.business || !filters.contract || !filters.date) {
			setBatches([])
			return
		}

		if (isFetchingRef.current) return
		isFetchingRef.current = true
		setLoading(true)
		dispatch(setLoader(true))

		try {
			const url = Endpoints.v2.uriRoutes({
				businessId: filters.business,
				contractId: filters.contract,
				date: new Date(filters.date).toISOString().split("T")[0],
				pageSize: 200,
			})
			const response = await API.get({ url })
			const routes: Route[] = response.data?.results?.routes ?? []

			const externalRoutes = routes.filter((r) => r.externalDelivery)
			const grouped = groupOrdersIntoBatches(externalRoutes)
			setBatches(grouped)
		} catch (error) {
			console.error("Error fetching external batches:", error)
			setBatches([])
		} finally {
			setLoading(false)
			dispatch(setLoader(false))
			isFetchingRef.current = false
		}
	}, [dispatch, filters.business, filters.contract, filters.date])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	// Reset selected vans when the filters change, since the available van options may change.
	useEffect(() => {
		setSelectedVans([])
	}, [filters.business, filters.contract, filters.date])

	useEffect(() => {
		delayedDispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.sidebar.externalDeliveries,
						icon: "route",
					},
				],
				actions: breadcrumbActions,
			}))
		)
	}, [breadcrumbActions])

	// Built from the unfiltered batches
	const vans = useMemo(() => {
		const byVehicleId = new Map<number, VanOption>()
		for (const batch of batches) {
			if (batch.pickup?.type !== "van") continue
			const { vanVehicleId, vanRouteName } = batch.pickup
			if (vanVehicleId === null || vanVehicleId === undefined) continue
			if (!byVehicleId.has(vanVehicleId)) {
				byVehicleId.set(vanVehicleId, {
					vehicleId: vanVehicleId,
					name: vanRouteName ?? null,
				})
			}
		}
		return Array.from(byVehicleId.values()).sort((a, b) =>
			String(a.name ?? a.vehicleId).localeCompare(String(b.name ?? b.vehicleId))
		)
	}, [batches])

	const filteredBatches = useMemo(() => {
		const term = searchTerm.toLowerCase()

		return batches.filter((b) => {
			if (
				selectedStatus.length > 0 &&
				!(b.batchingStatus && selectedStatus.includes(b.batchingStatus))
			) {
				return false
			}

			if (selectedVans.length > 0) {
				const matchesVan =
					b.pickup?.type === "van"
						? selectedVans.includes(String(b.pickup.vanVehicleId))
						: selectedVans.includes(WAREHOUSE_PICKUP_VALUE)
				if (!matchesVan) return false
			}

			if (!term) return true
			return (
				b.internalBatchId.toLowerCase().includes(term) ||
				b.partner.toLowerCase().includes(term) ||
				b.courier?.name?.toLowerCase().includes(term) ||
				b.routeName.toLowerCase().includes(term)
			)
		})
	}, [batches, searchTerm, selectedStatus, selectedVans])

	return (
		<>
			<Helmet>
				<title>{Strings.sidebar.externalDeliveries}</title>
			</Helmet>
			<ContentWrapper extraClass="ExternalBatchesWrapper">
				<div className="ExternalBatches">
					<div className="ExternalBatches__header">
						<ExternalDeliveryFilters
							selectedStatus={selectedStatus}
							selectedVans={selectedVans}
							vans={vans}
							onStatusChange={setSelectedStatus}
							onVansChange={setSelectedVans}
							loading={loading}
						/>
						<Search
							placeholder={Strings.generic.search}
							allowClear
							onChange={(e) => {
								if (!e.target.value) setSearchTerm("")
							}}
							onBlur={(e) => {
								if (e.target.value) {
									setSearchTerm(e.target.value)
								}
							}}
							className="ExternalBatches__search"
						/>
						{filteredBatches.length > 0 && (
							<Button
								icon={<DownloadOutlined />}
								onClick={() => generateAllBatchesPdf(filteredBatches)}
							>
								PDF
							</Button>
						)}
					</div>

					{loading ? (
						<div className="ExternalBatches__loading">
							<Spin size="large" />
						</div>
					) : filteredBatches.length === 0 ? (
						<div className="ExternalBatches__empty">
							<Empty description={Strings.routes.externalBatchesEmpty} />
						</div>
					) : (
						<div className="ExternalBatches__grid">
							{filteredBatches.map((batch) => (
								<BatchCard key={batch.internalBatchId} batch={batch} />
							))}
						</div>
					)}
				</div>
			</ContentWrapper>
		</>
	)
}

function generateAllBatchesPdf(batches: ExternalBatch[]) {
	const doc = new jsPDF("p", "mm", "a4")
	const pageWidth = doc.internal.pageSize.getWidth()
	const pageHeight = doc.internal.pageSize.getHeight()
	const margin = 12
	const contentWidth = pageWidth - margin * 2
	let y = margin

	const drawPageHeader = () => {
		doc.setFillColor(7, 90, 174)
		doc.rect(0, 0, pageWidth, 14, "F")
		doc.setFontSize(10)
		doc.setFont("helvetica", "bold")
		doc.setTextColor(255, 255, 255)
		doc.text("dingoo", margin, 10)
		doc.setFontSize(8)
		doc.setFont("helvetica", "normal")
		const headerSubtitle = Strings.sidebar.externalDeliveries
		const headerSubWidth = doc.getTextWidth(headerSubtitle)
		doc.text(headerSubtitle, pageWidth - margin - headerSubWidth, 10)
		doc.setTextColor(40, 40, 40)
	}

	const checkPage = (needed: number) => {
		if (y + needed > pageHeight - margin) {
			doc.addPage()
			drawPageHeader()
			y = 22
		}
	}

	const drawInfoRow = (label: string, value: string, col2X?: number) => {
		doc.setFontSize(8)
		doc.setTextColor(130, 130, 130)
		doc.setFont("helvetica", "normal")
		doc.text(label.toUpperCase(), col2X || margin, y)
		y += 4
		doc.setFontSize(10)
		doc.setTextColor(40, 40, 40)
		doc.setFont("helvetica", "normal")
		doc.text(value, col2X || margin, y)
		y += 7
	}

	// Document title header (first page only)
	doc.setFillColor(7, 90, 174) // Dingoo primary #075aae
	doc.rect(0, 0, pageWidth, 28, "F")
	// Accent strip below header
	doc.setFillColor(5, 70, 140)
	doc.rect(0, 28, pageWidth, 1.5, "F")

	doc.setFontSize(18)
	doc.setFont("helvetica", "bold")
	doc.setTextColor(255, 255, 255)
	doc.text("dingoo", margin, 14)

	doc.setFontSize(10)
	doc.setFont("helvetica", "normal")
	const subtitle = `${Strings.sidebar.externalDeliveries} — ${batches.length} batch${batches.length !== 1 ? "es" : ""}`
	doc.text(subtitle, margin, 22)

	// Date on the right
	const dateStr = new Date().toLocaleDateString([], {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	})
	doc.setFontSize(9)
	const dateWidth = doc.getTextWidth(dateStr)
	doc.text(dateStr, pageWidth - margin - dateWidth, 22)

	y = 38

	for (let i = 0; i < batches.length; i++) {
		const batch = batches[i]
		const partner =
			batch.partner.charAt(0).toUpperCase() + batch.partner.slice(1)

		// Check if there's enough space for a batch (~60mm minimum)
		if (i > 0) {
			checkPage(60)
			// Separator between batches
			y += 4
			doc.setDrawColor(7, 90, 174) // Dingoo primary
			doc.setLineWidth(0.3)
			doc.line(margin, y, margin + contentWidth, y)
			doc.setLineWidth(0.2)
			y += 8
		}

		// Header bar — Dingoo branded
		doc.setFillColor(7, 90, 174) // Dingoo primary #075aae
		doc.roundedRect(margin, y, contentWidth, 14, 2, 2, "F")
		doc.setFontSize(13)
		doc.setFont("helvetica", "bold")
		doc.setTextColor(255, 255, 255)
		doc.text(partner, margin + 6, y + 9)
		doc.setFontSize(10)
		doc.setFont("helvetica", "normal")
		const countText = `${batch.orderCount} ${batch.orderCount === 1 ? Strings.routes.externalBatchesOrder : Strings.routes.externalBatchesOrders}`
		doc.text(
			countText,
			margin + contentWidth - 6 - doc.getTextWidth(countText),
			y + 9
		)
		doc.setTextColor(40, 40, 40)
		y += 22

		const dataStartY = y

		// Batch ID
		doc.setFontSize(8)
		doc.setTextColor(130, 130, 130)
		doc.setFont("helvetica", "normal")
		doc.text("BATCH ID", margin, y)
		y += 4
		doc.setFontSize(10)
		doc.setTextColor(40, 40, 40)
		doc.setFont("courier", "bold")
		doc.text(batch.internalBatchId, margin, y)
		doc.setFont("helvetica", "normal")
		y += 8

		// Info grid — two columns
		const col2X = margin + contentWidth / 2

		drawInfoRow(Strings.routes.externalBatchesRoute, batch.routeName)

		if (batch.courier) {
			const savedY = y
			drawInfoRow(Strings.routes.externalDeliveryCourier, batch.courier.name)
			const afterLeftY = y

			y = savedY
			if (batch.courier.phoneNumber) {
				drawInfoRow(
					Strings.routes.externalBatchesPhone,
					batch.courier.phoneNumber,
					col2X
				)
			}
			y = Math.max(afterLeftY, y)

			const vehicle = [
				batch.courier.vehicleType,
				batch.courier.vehicleMake,
				batch.courier.vehicleModel,
				batch.courier.vehicleColor,
			]
				.filter(Boolean)
				.join(" ")
			if (vehicle) {
				drawInfoRow(Strings.routes.externalBatchesVehicle, vehicle)
			}
		}

		if (batch.pickupEta || batch.assignedAt) {
			const savedY = y
			if (batch.pickupEta) {
				const eta = new Date(batch.pickupEta).toLocaleTimeString([], {
					hour: "2-digit",
					minute: "2-digit",
				})
				drawInfoRow(Strings.routes.externalDeliveryPickupEta, eta)
			}
			const afterLeftY = y

			y = savedY
			if (batch.assignedAt) {
				const assigned = new Date(batch.assignedAt).toLocaleString([], {
					day: "2-digit",
					month: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				})
				drawInfoRow(Strings.routes.externalDeliveryAssignedAt, assigned, col2X)
			}
			y = Math.max(afterLeftY, y)
		}

		// Checkbox square on the right, aligned to top of data area
		const checkboxSize = 10
		const cbX = margin + contentWidth - checkboxSize
		const cbY = dataStartY
		doc.setDrawColor(7, 90, 174)
		doc.setLineWidth(0.5)
		doc.roundedRect(cbX, cbY, checkboxSize, checkboxSize, 1, 1, "D")

		// Mark with X if pickup is complete or beyond
		const pickedUpStatuses = [
			"pickup_complete",
			"out_for_delivery",
			"arriving_dropoff",
			"delivered",
			"returning",
			// Only reachable after pickup: a cancel before it is "cancelled"
			"not_delivered",
		]
		if (batch.latestStatus && pickedUpStatuses.includes(batch.latestStatus)) {
			doc.setLineWidth(0.8)
			doc.line(cbX + 2, cbY + 2, cbX + checkboxSize - 2, cbY + checkboxSize - 2)
			doc.line(cbX + checkboxSize - 2, cbY + 2, cbX + 2, cbY + checkboxSize - 2)
		}

		doc.setLineWidth(0.2)
	}

	// Add page numbers to all pages
	const totalPages = doc.getNumberOfPages()
	for (let p = 1; p <= totalPages; p++) {
		doc.setPage(p)
		doc.setFontSize(8)
		doc.setFont("helvetica", "normal")
		doc.setTextColor(150, 150, 150)
		const pageText = `${p} / ${totalPages}`
		const textWidth = doc.getTextWidth(pageText)
		doc.text(pageText, (pageWidth - textWidth) / 2, pageHeight - 10)
	}

	doc.save("external-batches.pdf")
}

const BatchCard: React.FC<{ batch: ExternalBatch }> = ({ batch }) => {
	const partner = batch.partner.charAt(0).toUpperCase() + batch.partner.slice(1)

	const vehicleInfo = batch.courier
		? [
				batch.courier.vehicleMake,
				batch.courier.vehicleModel,
				batch.courier.vehicleColor,
			]
				.filter(Boolean)
				.join(" ")
		: null

	const vehicleType = batch.courier?.vehicleType

	const formatEta = (eta?: string) => {
		if (!eta) return null
		try {
			return new Date(eta).toLocaleTimeString([], {
				hour: "2-digit",
				minute: "2-digit",
			})
		} catch {
			return eta
		}
	}

	const orderLabel =
		batch.orderCount === 1
			? Strings.routes.externalBatchesOrder
			: Strings.routes.externalBatchesOrders

	return (
		<div className="BatchCard">
			<div className="BatchCard__top">
				<div className="BatchCard__partner">
					<span className="BatchCard__partner-name">{partner}</span>
				</div>
				<div className="BatchCard__orders-count">
					<span className="BatchCard__orders-number">{batch.orderCount}</span>
					<span className="BatchCard__orders-label">{orderLabel}</span>
				</div>
			</div>

			<div className="BatchCard__id">
				<span className="BatchCard__label">Batch</span>
				<span className="BatchCard__value BatchCard__value--mono">
					{batch.internalBatchId}
				</span>
			</div>

			<div className="BatchCard__route">
				<span className="BatchCard__label">
					{Strings.routes.externalBatchesRoute}
				</span>
				<span className="BatchCard__value">{batch.routeName}</span>
			</div>

			{batch.returnPincode && (
				<div className="BatchCard__route">
					<span className="BatchCard__label">
						{Strings.routes.externalDeliveryReturnPincode}
					</span>
					<span className="BatchCard__value BatchCard__value--mono">
						{batch.returnPincode}
					</span>
				</div>
			)}

			{batch.batchingStatus && (
				<div className="BatchCard__status">
					<span className="BatchCard__label">
						{Strings.routes.externalDeliveryStatus}
					</span>
					<Tag
						color={BATCHING_STATUS_COLORS[batch.batchingStatus] || "default"}
					>
						{getBatchingStatusLabel(batch.batchingStatus)}
					</Tag>
				</div>
			)}

			<BatchProgress statuses={batch.orders.map((order) => order.status)} />

			{batch.courier && (
				<div className="BatchCard__courier">
					<div className="BatchCard__courier-header">
						<span className="BatchCard__label">
							{Strings.routes.externalDeliveryCourier}
						</span>
					</div>
					<div className="BatchCard__courier-info">
						<span className="BatchCard__courier-name">
							{batch.courier.name}
						</span>
						{batch.courier.phoneNumber && (
							<span className="BatchCard__courier-phone">
								{batch.courier.phoneNumber}
							</span>
						)}
					</div>
					{(vehicleInfo || vehicleType) && (
						<div className="BatchCard__vehicle">
							<span className="BatchCard__label">
								{Strings.routes.externalBatchesVehicle}
							</span>
							<span className="BatchCard__value">
								{vehicleType && (
									<span className="BatchCard__vehicle-type">{vehicleType}</span>
								)}
								{vehicleInfo && (
									<span className="BatchCard__vehicle-details">
										{vehicleInfo}
									</span>
								)}
							</span>
						</div>
					)}
				</div>
			)}

			<ExternalReturnsProgress
				entries={batch.returnEntries}
				labelClassName="BatchCard__label"
			/>

			{batch.pickupEta && (
				<div className="BatchCard__etas">
					{batch.pickupEta && (
						<div className="BatchCard__eta">
							<span className="BatchCard__label">
								{Strings.routes.externalDeliveryPickupEta}
							</span>
							<span className="BatchCard__value">
								{formatEta(batch.pickupEta)}
							</span>
						</div>
					)}
				</div>
			)}

			{batch.orders.length > 0 && (
				<Collapse ghost className="BatchCard__orders-collapse">
					<Collapse.Panel
						key="orders"
						header={
							<span className="BatchCard__orders-collapse-label">
								{Strings.routes.addresses} ({batch.orders.length})
							</span>
						}
					>
						<div className="BatchCard__orders-list">
							{batch.orders.map((order) => (
								<div key={order.orderId} className="BatchCard__order-item">
									<div className="BatchCard__order-header">
										<span className="BatchCard__order-id">{order.orderId}</span>
										{order.deliveryStatus && (
											<Tag
												color={
													EXTERNAL_STATUS_COLORS[order.deliveryStatus] ||
													"default"
												}
												style={{ marginRight: 0 }}
											>
												{getExternalStatusLabel(order.deliveryStatus)}
											</Tag>
										)}
									</div>
									<div className="BatchCard__order-details">
										<span className="BatchCard__order-user">
											{order.userName}
										</span>
										<span className="BatchCard__order-address">
											{order.address}
										</span>
										{order.dropoffEta && (
											<span className="BatchCard__order-eta">
												{Strings.routes.externalDeliveryDropoffEta}:{" "}
												{formatEta(order.dropoffEta)}
											</span>
										)}
										{order.returnState && (
											<span className="BatchCard__order-eta">
												{Strings.routes.externalReturnsStatus}:{" "}
												<ExternalReturnTag
													state={order.returnState}
													style={{ fontSize: 11 }}
												/>
											</span>
										)}
										{!batch.returnPincode && order.returnPincode && (
											<span className="BatchCard__order-eta">
												{Strings.routes.externalDeliveryReturnPincode}:{" "}
												{order.returnPincode}
											</span>
										)}
										{order.barcode && (
											<span className="BatchCard__order-eta">
												{Strings.routes.externalDeliveryBarcode}:{" "}
												{order.barcode}
											</span>
										)}
										{!!order.manifestItems?.length && (
											<span className="BatchCard__order-eta">
												{Strings.routes.externalDeliveryManifestItems}:{" "}
												{order.manifestItems
													.map((item) =>
														item.quantity && item.quantity > 1
															? `${item.name} × ${item.quantity}`
															: item.name
													)
													.join(", ")}
											</span>
										)}
										{order.trackingUrl && (
											<a
												href={order.trackingUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="BatchCard__tracking-link"
											>
												{Strings.routes.externalDeliveryTracking}
											</a>
										)}
									</div>
								</div>
							))}
						</div>
					</Collapse.Panel>
				</Collapse>
			)}

			{batch.assignedAt && (
				<div className="BatchCard__footer">
					<span className="BatchCard__assigned">
						{Strings.routes.externalDeliveryAssignedAt}{" "}
						{new Date(batch.assignedAt).toLocaleString([], {
							day: "2-digit",
							month: "2-digit",
							hour: "2-digit",
							minute: "2-digit",
						})}
					</span>
				</div>
			)}
		</div>
	)
}

export default ExternalDeliveries
